import * as motion from "motion/react-client";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { title } from "components/primitives";
import { VideoBanner } from "components/video-banner";
import clsx from "clsx";
import PrettyButton from "components/pretty-button";
import { NavLink, useLoaderData, useNavigate } from "@remix-run/react";
import { FaSquareXTwitter, FaTwitch } from "react-icons/fa6";
import { RiKickFill } from "react-icons/ri";
import { PrettyDate } from "components/date";
import { CloudflareImage } from "components/cloudflare-image";
import { siteConfig } from "config/site";

import topBg from "~/images/top-bg.png";
import topMobileBg from "~/images/top-bg-mobile.png";
import spreen from "~/images/spreen.png";
import { Image } from "@heroui/react";

export const meta: MetaFunction = () => {
	return [
		{ title: "MCA | Minecraft Community Awards" },
		{
			name: "description",
			content:
				"Un evento impulsado por la comunidad que celebra lo mejor de la creatividad de Minecraft. Son premios que reconocen todo, desde clips graciosos y construcciones épicas hasta series destacadas y contenido innovador de los creadores.",
		},
	];
};
interface ServerData {
	isProduction: boolean;
}

export async function loader({
	context,
}: LoaderFunctionArgs): Promise<ServerData> {
	const { env } = context.cloudflare;
	const isProduction = env.WORKER_ENV === "production";

	return { isProduction };
}

export default function Index() {
	const navigate = useNavigate();
	const data = useLoaderData<typeof loader>();

	const drawSocials = {
		hidden: { opacity: 0, scale: 1, y: 0 },
		hover: { scale: 1.4 },
		visible: (i: number) => {
			const delay = 1.2 + i * 0.5;
			const yDelay = i / 3;
			return {
				opacity: 1,
				scale: 1,
				y: [0, 5, 0],
				transition: {
					opacity: { delay, duration: 5 },
					y: {
						duration: 2,
						ease: "easeInOut",
						times: [0, 0.2, 0.5, 0.8, 1],
						repeat: Number.POSITIVE_INFINITY,
						repeatDelay: 0,
						delay: yDelay,
					},
				},
			};
		},
	};

	return (
		<main>
			<div className="mb-10">
				<VideoBanner />
				<section className="mt-0 xl:absolute xl:top-0 xl:right-0 z-10 container mx-auto pt-8 xl:pt-48 2xl:pt-96 max-w-[50%] xl:px-6 flex-grow">
					<div className="flex flex-col items-center justify-center gap-4 xl:py-10">
						<div className="inline-block max-w-xl text-center 2xl:text-left justify-start">
							<motion.div
								initial={{ x: "-60%", opacity: 0 }}
								animate={{ x: "0", opacity: 1 }}
								transition={{
									x: { delay: 1.2, type: "spring", duration: 1.5, bounce: 0 },
									opacity: { delay: 1, ease: "easeInOut", duration: 2 },
								}}
							>
								<span className={title({ size: "xl" })}>Abril&nbsp;</span>
								<PrettyDate />
							</motion.div>

							<motion.span
								initial={{ opacity: 0, scale: 0 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{
									scale: { delay: 0.5, type: "spring", duration: 5, bounce: 0 },
									opacity: { delay: 3, ease: "easeInOut", duration: 2 },
								}}
								className="text-foreground text-[2.3rem] lg:text-5xl leading-9 inline uppercase font-montserrat tracking-widest font-normal"
							>
								Participa hoy
							</motion.span>
							<div className="mt-12">
								<PrettyButton onClick={() => navigate("/vote")}>
									Vota ahora
								</PrettyButton>
							</div>
						</div>
					</div>
				</section>
			</div>

			<div className="relative">
				<div className={clsx("-z-10 absolute h-full w-full")}>
					<CloudflareImage
						className="inline xl:hidden object-cover h-full w-full"
						loading="eager"
						width={1080}
						height={1920}
						isCdn={data.isProduction}
						src={topMobileBg}
						alt=""
						aria-hidden
						sizes="100vw"
					/>
					<CloudflareImage
						className="hidden xl:inline object-cover h-full w-full"
						loading="eager"
						isCdn={data.isProduction}
						src={topBg}
						alt=""
						aria-hidden
						sizes="100vw"
					/>
				</div>

				<section className="z-10 container mx-auto pt-16 flex-grow">
					<div className="flex flex-col items-center justify-center gap-4 py-8 xl:py-10">
						<div className="inline-block px-4 max-w-xl xl:max-w-2xl text-center justify-center">
							<motion.span
								initial={{ opacity: 0 }}
								whileInView={{ opacity: 1 }}
								transition={{
									opacity: { delay: 0.1, ease: "easeInOut", duration: 2 },
								}}
								className={title({ size: "lg" })}
							>
								Acerca del
							</motion.span>
							<br />
							<motion.span
								initial={{ opacity: 0 }}
								whileInView={{ opacity: 1 }}
								transition={{
									opacity: { delay: 0.6, ease: "easeInOut", duration: 2 },
								}}
								className={title({
									class: "font-montserrat tracking-widest uppercase font-light",
									color: "gold",
									size: "sm",
								})}
							>
								premio MCA
							</motion.span>

							<div className="text-lg my-2 xl:my-10 text-justify font-montserrat">
								Los <b>Minecraft Community Awards</b> representan una oportunidad única
								para reconocer a los creadores de contenido más destacados dentro
								de la comunidad de Minecraft. Este evento, organizado por <b>Spreen</b>,
								busca destacar el talento, la originalidad y el impacto de quienes
								han dejado huella con su trabajo. <b>Las nominaciones son realizadas
								por la comunidad</b>, destacando series, clips y creadores que hayan
								sobresalido a lo largo de los años.
							</div>
						</div>
					</div>
				</section>
			</div>

			<div className="bg-gradient-to-b from-[#2a241d] to-black w-full pb-32">
				<section className="container mx-auto pt-16 flex-grow px-4 xl:max-w-5xl">
					<div className="flex flex-col items-center justify-center gap-4 py-8 xl:py-10">
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-14 items-center justify-center xl:justify-start w-full">
							<motion.div
								initial={{ opacity: 0 }}
								whileInView={{ opacity: 1 }}
								transition={{
									opacity: { delay: 1.2, ease: "easeInOut", duration: 2 },
								}}
								className="w-full"
								aria-hidden
							>
								<Image
									as={CloudflareImage}
									radius="none"
									width="100%"
									height="auto"
									isCdn={data.isProduction}
									src={spreen}
									alt="Minecraft Community Awards 2025"
									sizes="(max-width: 640px) 100vw, 300px"
									className="aspect-square object-contain object-top"
								/>
							</motion.div>

							<div className="flex flex-col items-center justify-center gap-3 col-span-2 h-full w-full text-center">
								<motion.span
									initial={{ opacity: 0 }}
									whileInView={{ opacity: 1 }}
									transition={{
										opacity: { delay: 0.1, ease: "easeInOut", duration: 2 },
									}}
									className={title({
										class: "font-display tracking-wide font-normal",
										size: "lg",
									})}
								>
									Host de los Premios
								</motion.span>
								<motion.span
									initial={{ opacity: 0 }}
									whileInView={{ opacity: 1 }}
									transition={{
										opacity: { delay: 0.3, ease: "easeInOut", duration: 2 },
									}}
									className={title({
										class:
											"font-montserrat tracking-widest uppercase font-light",
										color: "gold",
										size: "sm",
									})}
								>
									Spreen
								</motion.span>
								<div className="flex flex-row items-center justify-center mt-6 gap-5 text-4xl text-center font-montserrat">
									<motion.div
										whileHover="hover"
										initial="hidden"
										whileInView="visible"
										custom={0}
										variants={drawSocials}
									>
										<NavLink
											aria-label="Twitter"
											to={siteConfig.links.twitterSpreen}
										>
											<FaSquareXTwitter
												size={40}
												className="text-default-900"
											/>
										</NavLink>
									</motion.div>
									<motion.div
										whileHover="hover"
										initial="hidden"
										whileInView="visible"
										custom={1}
										variants={drawSocials}
									>
										<NavLink aria-label="Discord" to={siteConfig.links.twitch}>
											<FaTwitch size={40} className="text-default-900" />
										</NavLink>
									</motion.div>
									<motion.div
										whileHover="hover"
										initial="hidden"
										whileInView="visible"
										custom={2}
										variants={drawSocials}
									>
										<NavLink aria-label="Kick" to={siteConfig.links.kick}>
											<RiKickFill size={40} className="text-default-900" />
										</NavLink>
									</motion.div>
								</div>
							</div>
						</div>
					</div>
				</section>
			</div>
		</main>
	);
}
